import { Button } from "@/components/ui/button";
import useWarningBar from "@/hooks/useWarningBar";
import { LocalStorageKey } from "@/lib/localStorage";
import { cn } from "@/lib/utils";
import { BellOffIcon, CloudOffIcon, DownloadCloudIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FaDiscord } from "react-icons/fa";

const MAJOR_DISMISSAL_TIME = 12 * 60 * 60 * 1000;
const MINOR_DISMISSAL_TIME = 48 * 60 * 60 * 1000;

const getTsUpdateDismissed = () => {
    const stored = localStorage.getItem(LocalStorageKey.UpdateWarningPostponedTs);
    if (!stored) return false;
    const parsed = parseInt(stored);
    if (isNaN(parsed)) return false;
    return parsed;
}

const checkPostponeStatus = (isImportant: boolean) => {
    const tsLastDismissal = getTsUpdateDismissed();
    const tsNow = Date.now();
    const maxTime = isImportant ? MAJOR_DISMISSAL_TIME : MINOR_DISMISSAL_TIME;
    if (!tsLastDismissal || tsLastDismissal + maxTime < tsNow) {
        return true;
    }
    return false;
}

type InnerWarningBarProps = {
    titleIcon: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    isImportant: boolean;
    canPostpone: boolean;
};

export function InnerWarningBar({ titleIcon, title, description, isImportant, canPostpone }: InnerWarningBarProps) {
    const [rand, setRand] = useState(0);

    const forceRerender = () => {
        setRand(Math.random());
    }

    const postponeUpdate = () => {
        localStorage.setItem(LocalStorageKey.UpdateWarningPostponedTs, Date.now().toString());
        forceRerender()
    }

    useEffect(() => {
        const interval = setInterval(() => {
            forceRerender()
        }, 60_000);
        return () => clearInterval(interval);
    }, []);

    if (canPostpone && !checkPostponeStatus(isImportant)) return null;
    return (
        <div className='fixed top-[26px] z-40 flex w-full justify-center px-4 pointer-events-none'>
            <div className={cn(
                "fc-surface pointer-events-auto w-full sm:w-[28rem] overflow-hidden rounded-card border shadow-fc-modal",
                "group flex flex-col items-center justify-center px-4 py-2 cursor-default",
                isImportant ? 'border-fc-red/50' : 'border-fc-accent/45'
            )}>
                <h2 className={cn("flex items-center gap-2 text-center text-[13px] font-semibold", isImportant ? 'text-fc-red-soft' : 'text-fc-accent-bright')}>
                    {titleIcon}
                    {title}
                </h2>

                <span className='hidden group-hover:block text-center text-[12.5px] text-fc-txt2 pt-2'>
                    {description}
                    <div className="mt-3 flex flex-row items-center justify-center gap-2">
                        {canPostpone && <Button
                            size="xs"
                            variant="secondary"
                            onClick={() => postponeUpdate()}
                        >
                            <BellOffIcon className="h-[0.9rem]" /> Später
                        </Button>}

                        <Button
                            size="xs"
                            variant="secondary"
                            asChild
                        >
                            <a href="https://discord.gg/uAmsGa2" target="_blank">
                                <FaDiscord size="14" /> Upstream-Support
                            </a>
                        </Button>
                    </div>
                </span>
            </div>
        </div>
    );
}


export default function WarningBar() {
    const { offlineWarning, txUpdateData, fxUpdateData } = useWarningBar();

    if (offlineWarning) {
        return <InnerWarningBar
            titleIcon={<CloudOffIcon className="h-[1.1rem]" />}
            title="Verbindung zum FC-Admin-Core verloren."
            description={<>
                Die Socketverbindung zum FC-Admin-Core ist unterbrochen. <br />
                Falls FXServer beendet wurde, bitte neu starten.
            </>}
            isImportant={true}
            canPostpone={false}
        />
    } else if (txUpdateData) {
        return <InnerWarningBar
            titleIcon={<DownloadCloudIcon className="h-[1.1rem]" />}
            title={txUpdateData.isImportant
                ? 'Diese Version des FC-Admin-Cores ist veraltet.'
                : 'Ein Core-Patch für FC Admin ist verfügbar.'}
            description={txUpdateData.isImportant
                ? `Version v${txUpdateData.version} bringt neue Funktionen, Fehlerbehebungen und Verbesserungen.`
                : `Bei Problemen bitte auf v${txUpdateData.version} aktualisieren.`}
            isImportant={txUpdateData.isImportant}
            canPostpone={true}
        />
    } else if (fxUpdateData) {
        return <InnerWarningBar
            titleIcon={<DownloadCloudIcon className="h-[1.1rem]" />}
            title={fxUpdateData.isImportant
                ? 'Diese FXServer-Version ist veraltet.'
                : 'Ein FXServer-Update ist verfügbar.'}
            description={`Bitte FXServer auf Artifact ${fxUpdateData.version} aktualisieren.`}
            isImportant={fxUpdateData.isImportant}
            canPostpone={true}
        />
    } else {
        return null;
    }
}
